import { css } from '@emotion/react';
import { faMap } from '@fortawesome/pro-solid-svg-icons';
import { type FC } from 'react';
import correctSpreadsheetExample from '~/_shared/../../../assets/images/correct_spreadsheet_example.png';
import { ButtonComponent } from '~/_shared/baseComponents/buttons/button/button.component';
import {
  LabelInShapeComponent, Shape,
} from '~/_shared/baseComponents/labelInShape/labelInShapeComponent';
import { Stack } from '~/_shared/baseComponents/layout/stack.component';
import { ImageComponent } from '~/_shared/components/image/image.component';
import { ModalComponent } from '~/_shared/components/modal/modal.component';
import { useTheme } from '~/_shared/themes/theme.hooks';
import { useTranslation } from '~/_shared/utils/hooks';
import {
  type ModalProps, ModalType,
} from '~/modal/modalType.enum';
import { useModal } from '~/modal/useModal.hook';
import type { Translations } from '~/translations/translations.type';

const STEPS = ['A', 'B', 'C', 'D'] as const;

const STEP_TRANSLATION_KEYS: { readonly [K in typeof STEPS[number]]: keyof Translations } = {
  A: 'prepareData.stepA',
  B: 'prepareData.stepB',
  C: 'prepareData.stepC',
  D: 'prepareData.stepD',
};

const STEP_TILE_SIZE = 22;

const contentStyle = css({
  padding: '16px 24px',
});

const introTextStyle = css({
  fontSize: 14,
  margin: 0,
});

const imageWrapperStyle = css({
  marginTop: 4,
});

const imageStyle = css({
  background: 'none',
});

const stepsContainerStyle = css({
  marginTop: 8,
});

const stepRowStyle = css({
  alignItems: 'center',
  display: 'flex',
  gap: 8,
});

const stepTileStyle = css({
  fontSize: 14,
  fontWeight: 600,
});

const stepTextStyle = css({
  fontSize: 14,
  lineHeight: '20px',
  margin: 0,
});

const helpTipStyle = css({
  fontSize: 14,
  marginTop: 24,
});

export type PrepareDataModalComponentProps = ModalProps;

export const PrepareDataModalComponent: FC<PrepareDataModalComponentProps> = ({
  isOpen,
  onClose,
}) => {
  const [t] = useTranslation();
  const theme = useTheme();
  const { replaceCurrentModal: openCreateMapModal } = useModal(ModalType.CreateNewMap);

  const tileColors = {
    fillColor: theme.iconColors.focused,
    fontColor: theme.textColors.quaternary,
  };

  return (
    <ModalComponent
      caption={t('prepareData.title')}
      confirmButton={(
        <ButtonComponent
          prefixIcon={faMap}
          text={t('prepareData.confirm')}
          onClick={() => openCreateMapModal({})}
        />
      )}
      contentStyle={contentStyle}
      isOpen={isOpen}
      onClose={onClose}
    >
      <p css={introTextStyle}>{t('prepareData.introText')}</p>

      <div css={imageWrapperStyle}>
        <ImageComponent
          alt="Data preparation example"
          css={imageStyle}
          src={correctSpreadsheetExample}
          width="100%"
        />
      </div>

      <Stack
        css={stepsContainerStyle}
        gap={8}
      >
        {STEPS.map(step => (
          <div
            css={stepRowStyle}
            key={step}
          >
            <LabelInShapeComponent
              colors={tileColors}
              css={stepTileStyle}
              shape={Shape.square}
              size={STEP_TILE_SIZE}
            >
              {step}
            </LabelInShapeComponent>
            <p css={stepTextStyle}>{t(STEP_TRANSLATION_KEYS[step])}</p>
          </div>
        ))}
      </Stack>

      <p css={helpTipStyle}>
        {t('prepareData.helpTip')}
      </p>
    </ModalComponent>
  );
};
